import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Eye, Flame, Loader2, MapPin, Package, Send } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs } from "@/components/layout/Crumbs";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { ListingCard, money } from "@/vault/marketplace/Marketplace";

const KIND_WORDS = { machine: "machines", supply: "supplies", blank: "blanks", apparel: "apparel", class: "classes", wanted: "wanted ads" };

const inputCls = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:border-zinc-900 focus:outline-none";

export default function Listing() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [shot, setShot] = useState(0);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/marketplace/listings/${slug}`).then(({ data }) => setData(data))
            .catch(() => toast.error("That listing could not be found")).finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
    }, [user]);

    const send = async () => {
        if (!form.name || !form.email || !form.message) { toast.error("Name, email and a message are required"); return; }
        setSending(true);
        try {
            await api.post(`/marketplace/listings/${data.listing.id}/inquiries`, form);
            setSent(true);
            toast.success("Message sent to the seller");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setSending(false); }
    };

    if (loading) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;
    if (!data) return (
        <Layout><div className="mx-auto max-w-3xl px-4 py-24 text-center">
            <h1 className="font-display text-3xl font-black">Listing not found</h1>
            <Link to="/supplies" className="mt-4 inline-block text-zinc-900 underline">Back to the marketplace</Link>
        </div></Layout>
    );

    const l = data.listing;

    return (
        <Layout>
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">

                <div className="mb-4"><Crumbs trail={[{ label: "Classifieds", to: "/supplies" }, { label: KIND_WORDS[l.kind] || "Listings", to: `/supplies?kind=${l.kind}` }, { label: l.title }]} /></div>
                <Link to={`/supplies?kind=${l.kind}`} className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-zinc-900" data-testid="back-to-marketplace">
                    <ArrowLeft size={15} /> Back to {KIND_WORDS[l.kind] || "listings"}
                </Link>

                <div className="mt-5 grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="overflow-hidden rounded-3xl border border-white/10 bg-black/50">
                            {l.images?.length ? (
                                <img src={mediaUrl(l.images[shot])} alt={l.title} className="max-h-[520px] w-full object-cover" data-testid="listing-photo" />
                            ) : (
                                <div className="flex h-64 items-center justify-center text-zinc-600"><Package size={40} /></div>
                            )}
                        </div>
                        {l.images?.length > 1 && (
                            <div className="mt-3 flex gap-2">
                                {l.images.map((img, i) => (
                                    <button key={i} onClick={() => setShot(i)} data-testid={`listing-thumb-${i}`}
                                        className={`h-16 w-20 overflow-hidden rounded-xl border ${shot === i ? "border-zinc-900" : "border-white/12"}`}>
                                        <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                        <p className="mt-6 whitespace-pre-line text-[15px] leading-relaxed text-zinc-300" data-testid="listing-description">{l.description}</p>
                        {l.kind !== "wanted" && l.kind !== "class" && l.location && (
                            <p className="mt-4 text-[15px] font-semibold text-zinc-200" data-testid="listing-pickup-note">
                                Buyers pick up in {l.location}.
                            </p>
                        )}
                    </div>

                    <div className="lg:col-span-5">
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 lg:sticky lg:top-24">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded-full border border-white/20 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-zinc-300">{l.condition}</span>
                                {l.featured && <span className="inline-flex items-center gap-1 rounded-full bg-zinc-900 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-white"><Flame size={11} /> Featured</span>}
                                {l.status === "sold" && <span className="rounded-full bg-red-500/20 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-red-400">Sold</span>}
                            </div>
                            <h1 className="mt-3 font-display text-3xl font-black leading-tight tracking-tight" data-testid="listing-title">{l.title}</h1>
                            <p className="mt-2 font-mono text-4xl font-black text-zinc-900" data-testid="listing-price">{money(l.price)}</p>
                            {l.accept_offers && l.price !== null && <p className="mt-1 text-xs text-zinc-500">Offers welcome</p>}

                            <div className="mt-5 space-y-2 border-t border-white/10 pt-5 text-sm text-zinc-400">
                                {l.kind === "class" && l.duration && <p><span className="text-zinc-500">Length:</span> <span className="text-white">{l.duration}</span></p>}
                                {l.kind === "class" && l.capacity && <p><span className="text-zinc-500">Seats:</span> <span className="text-white">{l.capacity} per session</span></p>}                                {l.brand && <p><span className="text-zinc-500">Brand:</span> <span className="text-white">{l.brand}</span></p>}
                                {l.quantity > 1 && <p><span className="text-zinc-500">In stock:</span> <span className="text-white">{l.quantity} {l.unit || "pcs"}</span></p>}
                                <p className="flex items-center gap-1.5"><MapPin size={13} className="text-zinc-900" /> {l.location}</p>
                                <p className="flex items-center gap-1.5"><Eye size={13} /> {l.views} views · {l.inquiry_count || 0} inquiries</p>
                                <p className="flex items-center gap-1.5 font-semibold text-white" data-testid="listing-seller">
                                    {l.seller_kind === "shop" && <BadgeCheck size={15} className="text-zinc-900" />} {l.seller_name}
                                    {l.seller_kind === "shop" && <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-900">verified shop</span>}
                                </p>
                            </div>

                            {sent ? (
                                <div className="mt-6 rounded-2xl border border-zinc-300 bg-zinc-100 p-4 text-sm text-zinc-200" data-testid="inquiry-sent">
                                    Message sent. {l.seller_name} gets it by email and can reply to you directly.
                                </div>
                            ) : (
                                <div className="mt-6 space-y-3" data-testid="inquiry-form">
                                    <p className="font-display text-lg font-bold">{l.kind === "class" ? "Message the teacher" : "Message the seller"}</p>
                                    <input className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inquiry-name" />
                                    <input className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email" />
                                    <input className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" />
                                    <textarea className={inputCls} rows={4} placeholder="Is this still available? I can pick up this week…"
                                        value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquiry-message" />
                                    <GreenButton className="w-full" onClick={send} disabled={sending || l.status === "sold"} data-testid="inquiry-submit">
                                        {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send message
                                    </GreenButton>
                                    <p className="text-[11px] text-zinc-500">We pass your details straight to the seller. Never wire money before you see the machine.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {data.similar?.length > 0 && (
                    <div className="mt-14">
                        <h2 className="font-display text-2xl font-black tracking-tight">More {KIND_WORDS[l.kind] || "listings"}</h2>
                        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                            {data.similar.map((s) => <ListingCard key={s.slug} l={s} />)}
                        </div>
                    </div>
                )}
            </section>
        </Layout>
    );
}
